import { Link } from 'react-router-dom';
import type { StorefrontSection } from '@mlm/shared';
import { useFlashSale, useProductHref } from '../../lib/hooks';
import { peso } from '../../lib/format';
import { Placeholder } from '../placeholder';
import { Countdown } from '../countdown';

type FlashSaleConfig = Extract<StorefrontSection, { type: 'flashSale' }>['config'];

/**
 * Flash-sale strip — renders only when the admin has configured + enabled it
 * (same internal gate as before). Extracted verbatim from home.tsx.
 */
export function FlashSaleSection({ section }: { section: StorefrontSection }) {
  const hrefFor = useProductHref();
  const cfg = section.config as FlashSaleConfig;
  const flash = useFlashSale();
  if (!flash.enabled) return null;
  const title = cfg.title ?? 'Flash Sale';
  const seeAllHref = cfg.seeAllHref ?? '/official';

  return (
    <section className="mt-4 rounded-[4px] bg-white">
      <div className="flex flex-wrap items-center gap-[18px] p-4">
        <span className="text-[21px] font-extrabold text-[#333]">{title}</span>
        <span className="flex items-center gap-1.5 text-[13px] font-bold text-o">
          <i className="omni-pulse h-[7px] w-[7px] rounded-full bg-o" /> On Sale Now
        </span>
        {flash.endsAt && <Countdown endsAt={flash.endsAt} />}
        <Link to={seeAllHref} className="ml-auto text-[13px] font-semibold text-o">
          SHOP ALL PRODUCTS ›
        </Link>
      </div>
      <div className="grid grid-cols-3 px-2 pb-4 lg:grid-cols-6">
        {flash.products.map((p, i) => (
          <Link key={p.id} to={hrefFor(p)} className="group block p-2">
            <div className="relative aspect-square overflow-hidden rounded-[4px] transition-shadow group-hover:shadow-[0_6px_16px_rgba(0,0,0,.12)]">
              <Placeholder i={i} src={p.image} alt={p.name} />
            </div>
            <div className="mt-[7px] line-clamp-2 h-[33px] text-[12.5px] leading-[1.3] text-[#333]">
              {p.name}
            </div>
            <div className="mt-1 text-[17px] font-extrabold text-o">
              {peso(p.price)}
              {p.was > 0 && (
                <span className="ml-1.5 text-xs font-medium text-[#aaa] line-through">
                  {peso(p.was)}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
